import { createWebHistory, createRouter } from "vue-router";
import api from "../config/axios.js";
import { ACCESS_PAGES, canAccessPage, PUBLIC_PATHS } from "../utils/accessControl";

import HomePage from "../pages/Home/HomePage.vue";
import DashboardPage from "../pages/Dashboard/DashboardPage.vue";
import PaymentPage from "../pages/Payment/PaymentPage.vue";
import LoginPage from "../pages/Authentication/LoginPage.vue";
import RegisterPage from "../pages/Authentication/RegisterPage.vue";
import ForgotPasswordPage from "../pages/Authentication/ForgotPasswordPage.vue";
import ForgotPasswordSendEmailPage from "../pages/Authentication/ForgotPasswordSendEmailPage.vue";
import TermsPage from "../pages/TermsPage.vue";
import PrivacyPolicyPage from "../pages/PrivacyPolicyPage.vue";
import ErrorPage from "../pages/ErrorPage.vue";
import FinancialManagementPage from "../pages/Financial/FinancialManagementPage.vue";
import RecurringExpensesPage from "../pages/Financial/RecurringExpensesPage.vue";
import FinancialGoalsPage from "../pages/Financial/FinancialGoalsPage.vue";
import FinancialAdvisorPage from "../pages/Financial/FinancialAdvisorPage.vue";
import Configurations from "../pages/Configuration/ConfigurationPage.vue";
import AccessDeniedPage from "../pages/AccessDeniedPage.vue";

const routes = [
  {
    path: "/",
    name: "HomePage",
    component: HomePage,
  },
  {
    path: "/dashboard",
    name: "DashboardPage",
    component: DashboardPage,
    meta: { accessKey: "dashboard" },
  },
  {
    path: "/payment",
    name: "PaymentPage",
    component: PaymentPage,
    meta: { accessKey: "payment" },
  },
  {
    path: "/financial-management",
    name: "FinancialManagementPage",
    component: FinancialManagementPage,
    meta: { accessKey: "transactions" },
  },
  {
    path: "/recurring-expenses",
    name: "RecurringExpensesPage",
    component: RecurringExpensesPage,
    meta: { accessKey: "recurring-expenses" },
  },
  {
    path: "/financial-goals",
    name: "FinancialGoalsPage",
    component: FinancialGoalsPage,
    meta: { accessKey: "goals" },
  },
  {
    path: "/financial-advisor",
    name: "FinancialAdvisorPage",
    component: FinancialAdvisorPage,
    meta: { accessKey: "ai-advisor" },
  },
  {
    path: "/configurations",
    name: "Configurations",
    component: Configurations,
    meta: { accessKey: "configurations" },
  },
  {
    path: "/access-denied",
    name: "AccessDeniedPage",
    component: AccessDeniedPage,
  },
  {
    path: "/login",
    name: "LoginPage",
    component: LoginPage,
  },
  {
    path: "/register",
    name: "RegisterPage",
    component: RegisterPage,
  },
  {
    path: "/forgot-password",
    name: "ForgotPasswordPage",
    component: ForgotPasswordPage,
  },
  {
    path: "/send-email-forgot-password",
    name: "ForgotPasswordSendEmailPage",
    component: ForgotPasswordSendEmailPage,
  },
  {
    path: "/terms",
    name: "TermsPage",
    component: TermsPage,
  },
  {
    path: "/privacy-policy",
    name: "PrivacyPolicyPage",
    component: PrivacyPolicyPage,
  },
  {
    path: "/:pathMatch(.*)*",
    name: "ErrorPage",
    component: ErrorPage
  }
];

const router = createRouter({
  history: createWebHistory(),
  linkExactActiveClass: "active",
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        top: 86,
        behavior: "smooth",
      };
    }

    return { top: 0, behavior: "smooth" };
  },
});

router.beforeEach(async (to, from) => {
  if (to.path === from.path && to.hash && to.hash !== from.hash) {
    return true;
  }

  const isPublicRoute = PUBLIC_PATHS.includes(to.path);

  if (to.path === "/access-denied") {
    return true;
  }

  try {
    const response = await api.axios.get("/api/user");
    const allowedPages = Array.isArray(response.data?.allowed_pages) ? response.data.allowed_pages : [];

    if (isPublicRoute) {
      return to.path === "/dashboard" ? true : "/dashboard";
    }

    const accessKey = to.meta?.accessKey as string | undefined;
    if (!canAccessPage(allowedPages, accessKey)) {
      const page = ACCESS_PAGES.find((item) => item.key === accessKey);

      return {
        path: "/access-denied",
        query: {
          module: page?.label || "este modulo",
          key: accessKey || "",
        },
      };
    }

    return true;
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;

    if (status === 401) {
      return isPublicRoute ? true : "/login";
    }

    if (status === 403) {
      const accessKey = to.meta?.accessKey as string | undefined;
      const page = ACCESS_PAGES.find((item) => item.key === accessKey);

      return isPublicRoute ? true : {
        path: "/access-denied",
        query: {
          module: page?.label || "este modulo",
          key: accessKey || "",
        },
      };
    }

    return isPublicRoute ? true : "/access-denied";
  }
});

export default router;
